/**
 * Animation class, used for animating elements.
 * The class can be used to animate against different progress events such as scroll position.
 * @alias Animation.Animation
 * @memberOf Animation
 */
export default class Animation {
  /**
   * @param {Animation.Timer.Timer} timer
   * @param easing
   */
  constructor(timer, easing = null) {
    // region Define fields.
    /**
     * @type {Animation.Timer.Timer} Used during the animation to call the ticker function.
     * @private
     */
    this._timer = timer;
    /**
     * @callback EasingCallbackClosure
     * Method is called to apply easing to the animation.
     * @param {number} rawPercentComplete
     * @return {number} The eased percent complete.
     */
    /**
     * @type {EasingCallbackClosure} The easing function to be used in the animation.
     * @private
     */
    this._easing = easing;

    /**
     * @callback AnimationCallbackClosure
     * Method is called to update elements to animate them.
     * @param {number} percentComplete
     * @param {object} data, from the {@link Animation.Timer.Timer} class.
     */
    /**
     * Animation callback closure, called to update the elements.
     * @type {AnimationCallbackClosure|null}
     * @private
     */
    this._animationCallback = null;
    // endregion
  }

  /**
   * Set the animation callback function.
   * @param {AnimationCallbackClosure} animationCallback Callback which is called to update the elements to animate them.
   */
  setAnimationCallback(animationCallback) {
    this._animationCallback = animationCallback;
    if (animationCallback === null) {
      this._timer.setTickerCallback(null);
    } else {
      this._timer.setTickerCallback((percent, data) => { this._tick(percent, data); });
    }
  }

  /**
   * The tick function called by a timer.
   * @param {number} percent
   * @param {object} data
   * @private
   */
  _tick(percent, data) {
    if (this._animationCallback !== null) {
      this._animationCallback(this._easing === null ? percent : this._easing(percent), data);
    }
  }

  /**
   * Destroys the Animation and attached timer. This removes things such as event listeners.
   */
  destroy() {
    if (this._timer !== null) {
      this._timer.destroy();
    }
  }
};



// WEBPACK FOOTER //
// ./src/class/Animation/Animation.js